import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
	DialogFooter,
} from '@/common/ui/dialog'
import { Button } from '@/common/ui/button'
import { Alert, AlertDescription } from '@/common/ui/alert'
import { AlertTriangle, Info } from 'lucide-react'

interface ProviderPairWarningDialogProps {
	open: boolean
	onOpenChange: (open: boolean) => void
	providerName: string
	pairedProviderName: string
	currentStatus: boolean
	newStatus: boolean
	pairedProviderStatus: boolean
	onConfirm: () => void
	isLoading?: boolean
}

export function ProviderPairWarningDialog({
	open,
	onOpenChange,
	providerName,
	pairedProviderName,
	currentStatus,
	newStatus,
	pairedProviderStatus,
	onConfirm,
	isLoading = false,
}: ProviderPairWarningDialogProps) {
	const willActivate = !currentStatus && newStatus
	const willDeactivate = currentStatus && !newStatus

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-lg">
				<DialogHeader>
					<DialogTitle className="flex items-center gap-2">
						<AlertTriangle className="h-5 w-5 text-orange-500" />
						แจ้งเตือน: ค่ายเกมคู่ (Paired Providers)
					</DialogTitle>
					<DialogDescription>
						คุณกำลังเปลี่ยนสถานะของค่ายเกมที่มีเวอร์ชันคู่กัน
					</DialogDescription>
				</DialogHeader>

				<div className="space-y-4 py-4">
					{/* Current Status */}
					<Alert>
						<Info className="h-4 w-4" />
						<AlertDescription className="space-y-2">
							<div className="font-medium">สถานะปัจจุบัน:</div>
							<div className="space-y-1 text-sm">
								<div className="flex items-center justify-between">
									<span className="font-medium">{providerName}</span>
									<span
										className={
											currentStatus ? 'text-green-600' : 'text-gray-500'
										}
									>
										{currentStatus ? '✓ เปิดใช้งาน' : '✗ ปิดใช้งาน'}
									</span>
								</div>
								<div className="flex items-center justify-between">
									<span className="font-medium">{pairedProviderName}</span>
									<span
										className={
											pairedProviderStatus ? 'text-green-600' : 'text-gray-500'
										}
									>
										{pairedProviderStatus ? '✓ เปิดใช้งาน' : '✗ ปิดใช้งาน'}
									</span>
								</div>
							</div>
						</AlertDescription>
					</Alert>

					{/* Warning Message */}
					<Alert className="border-orange-200 bg-orange-50 dark:border-orange-800 dark:bg-orange-950">
						<AlertTriangle className="h-4 w-4 text-orange-600" />
						<AlertDescription className="space-y-2 text-orange-900 dark:text-orange-100">
							<div className="font-medium">⚠️ คำเตือนสำคัญ:</div>
							<div className="space-y-1 text-sm">
								<p>
									• <strong>{providerName}</strong> และ{' '}
									<strong>{pairedProviderName}</strong>{' '}
									เป็นค่ายเกมเวอร์ชันคู่กัน
								</p>
								<p>• ต้องเปิด/ปิดใช้งานพร้อมกันเสมอ</p>
								<p>
									• หากเปิดเพียงค่ายเดียว{' '}
									<span className="font-semibold text-red-600 dark:text-red-400">
										ผู้เล่นจะไม่สามารถเล่นเกมได้
									</span>
								</p>
							</div>
						</AlertDescription>
					</Alert>

					{/* Recommendation */}
					<Alert className="border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950">
						<Info className="h-4 w-4 text-blue-600" />
						<AlertDescription className="text-blue-900 dark:text-blue-100">
							<div className="font-medium">💡 แนะนำ:</div>
							<div className="mt-1 text-sm">
								{willActivate && (
									<p>
										กรุณา{' '}
										<strong className="text-green-600 dark:text-green-400">
											เปิดใช้งาน
										</strong>{' '}
										ทั้ง <strong>{providerName}</strong> และ{' '}
										<strong>{pairedProviderName}</strong> พร้อมกัน
									</p>
								)}
								{willDeactivate && (
									<p>
										กรุณา{' '}
										<strong className="text-red-600 dark:text-red-400">
											ปิดใช้งาน
										</strong>{' '}
										ทั้ง <strong>{providerName}</strong> และ{' '}
										<strong>{pairedProviderName}</strong> พร้อมกัน
									</p>
								)}
								{!willActivate && !willDeactivate && (
									<p>
										กรุณาให้ทั้ง <strong>{providerName}</strong> และ{' '}
										<strong>{pairedProviderName}</strong>{' '}
										{newStatus ? (
											<strong className="text-green-600 dark:text-green-400">
												เปิดใช้งาน
											</strong>
										) : (
											<strong className="text-red-600 dark:text-red-400">
												ปิดใช้งาน
											</strong>
										)}{' '}
										พร้อมกัน
									</p>
								)}
							</div>
						</AlertDescription>
					</Alert>
				</div>

				<DialogFooter>
					<Button
						type="button"
						variant="outline"
						onClick={() => onOpenChange(false)}
						disabled={isLoading}
					>
						ยกเลิก
					</Button>
					<Button
						type="button"
						variant={willDeactivate ? 'destructive' : 'default'}
						onClick={onConfirm}
						disabled={isLoading}
					>
						{isLoading
							? 'กำลังดำเนินการ...'
							: `ยืนยัน${willActivate ? 'เปิด' : 'ปิด'}ใช้งาน`}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}
